<template>
  <modal name="securone-invite" class="modal-inner" transition="pop-out" :width="620" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" @before-close="reset">
    <span class="close-button" @click="$modal.hide('securone-invite')"><BaseIcon icon="times-circle" class="text-white" /></span>
    <div class="modal-header text-lg font-serif">{{ $t("securone.invite.title") }}</div>
    <div class="modal-body">
      <!-- D-31: segmented, basic user or SMS contact -->
      <div class="sd-tabs inline-flex mb-4">
        <button class="sd-tabs__trigger" :class="{ 'sd-tabs__trigger--active': kind === 'basic' }" @click="kind = 'basic'"><BaseIcon icon="user" class="mr-2" />{{ $t("securone.invite.basic_user") }}</button>
        <button class="sd-tabs__trigger" :class="{ 'sd-tabs__trigger--active': kind === 'sms' }" @click="kind = 'sms'"><BaseIcon icon="sms" class="mr-2" />{{ $t("securone.invite.sms_contact") }}</button>
      </div>

      <ValidationObserver ref="form">
        <div class="flex flex-wrap -mx-2">
          <template v-if="kind === 'basic'">
            <div class="w-full md:w-1/2 px-2 py-2"><BaseInput v-model="form.firstname" :field_name="$t('securone.invite.first_name')" rules="required" /></div>
            <div class="w-full md:w-1/2 px-2 py-2"><BaseInput v-model="form.lastname" :field_name="$t('securone.invite.last_name')" /></div>
            <div class="w-full md:w-1/2 px-2 py-2"><BaseInput v-model="form.email" type="email" :field_name="$t('securone.invite.email')" rules="required|email" /></div>
            <div class="w-full md:w-1/2 px-2 py-2"><BaseInput v-model="form.phone" type="tel" :field_name="$t('securone.invite.phone')" /></div>
          </template>
          <template v-else>
            <div class="w-full md:w-1/2 px-2 py-2"><BaseInput v-model="form.name" :field_name="$t('securone.invite.name')" rules="required" /></div>
            <div class="w-full md:w-1/2 px-2 py-2"><BaseInput v-model="form.phone" type="tel" :field_name="$t('securone.invite.phone')" rules="required|min:5" /></div>
          </template>
          <!-- mandatory department picker on both (spec §9 D-31) -->
          <div class="w-full px-2 py-2">
            <DepartmentPicker ref="departments" v-model="form.departmentIds" :label="$t('securone.department.label')" />
          </div>
          <div class="w-full px-2 py-2" v-if="groups.length">
            <div class="text-sm font-semibold text-secondary-800 pb-2">{{ $t("securone.invite.groups_optional") }}</div>
            <div class="flex flex-wrap gap-2">
              <label v-for="g in groups" :key="g._id" class="inline-flex items-center gap-2 border rounded px-2 py-1 text-sm cursor-pointer" :class="form.groups.includes(g._id) ? 'border-secondary-400 bg-secondary-50' : 'border-secondary-300'">
                <input type="checkbox" class="form-checkbox h-4 w-4" :value="g._id" v-model="form.groups" />{{ g.name }}
              </label>
            </div>
          </div>
        </div>
      </ValidationObserver>

      <SoNote tone="info" class="mt-3">{{ kind === "basic" ? $t("securone.invite.basic_note") : $t("securone.invite.sms_note") }}</SoNote>
      <!-- V10 banner: customer and department users are created in SecurCloud -->
      <SoNote tone="warn" class="mt-3">{{ $t("securone.invite.securcloud_note") }}</SoNote>
      <SoNote v-if="error" tone="bad" class="mt-3">{{ error }}</SoNote>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button class="sd-btn sd-btn--secondary float-left" @click="$modal.hide('securone-invite')">{{ $t("securone.common.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
        <button class="sd-btn sd-btn--success float-right" :disabled="busy" @click="submit">{{ kind === "basic" ? $t("securone.invite.send_invitation") : $t("securone.invite.add_contact") }}<BaseIcon icon="paper-plane" class="ml-1" /></button>
      </div>
    </div>
  </modal>
</template>

<script>
import SoNote from "./SoNote.vue";
import DepartmentPicker from "./DepartmentPicker.vue";
import api from "../securone.api";
import { errorMessage } from "../securone.helpers";

// D-31. Basic users receive a welcome mail and show as invited · pending until
// first sign-in. SMS contacts never sign in. Customer and department users are
// created in SecurCloud only (V10).
export default {
  name: "InviteUserModal",
  components: { SoNote, DepartmentPicker },
  props: {
    groups: { type: Array, default: () => [] },
  },
  data() {
    return { kind: "basic", form: this.emptyForm(), busy: false, error: null };
  },
  methods: {
    emptyForm() {
      return { firstname: "", lastname: "", email: "", phone: "", name: "", departmentIds: [], groups: [] };
    },
    open(kind = "basic") {
      this.reset();
      this.kind = kind;
      this.$modal.show("securone-invite");
    },
    reset() {
      this.form = this.emptyForm();
      this.busy = false;
      this.error = null;
      if (this.$refs.form) this.$refs.form.reset();
    },
    async submit() {
      if (!(await this.$refs.form.validate())) return;
      if (!this.form.departmentIds.length) {
        if (this.$refs.departments) this.$refs.departments.touched = true;
        return;
      }
      this.busy = true;
      this.error = null;
      try {
        const body = this.kind === "basic"
          ? { kind: "basic", firstname: this.form.firstname, lastname: this.form.lastname, email: this.form.email, phone: this.form.phone, departmentIds: this.form.departmentIds, groups: this.form.groups }
          : { kind: "sms", name: this.form.name, phone: this.form.phone, departmentIds: this.form.departmentIds, groups: this.form.groups };
        const result = await api.invite(body);
        this.$modal.hide("securone-invite");
        this.$emit("invited", result);
      } catch (error) {
        this.error = errorMessage(this, error);
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
